<template>
    <v-card class="mb-5 pa-2">
        <card-header
            :title="
                $t('modules.form.texts.title', {
                    'title-prefix': titlePrefix,
                })
            "
        />

        <template v-if="!allModules.length">
            <v-container fluid class="fill-height">
                <v-row justify="center" align="center">
                    <v-col class="text-center" xs="12" md="12">
                        <v-progress-circular
                            :size="50"
                            color="primary"
                            indeterminate
                        ></v-progress-circular>
                    </v-col>

                    <v-col class="text-center">
                        {{ $t('modules.form.texts.loading') }}
                    </v-col>
                </v-row>
            </v-container>
        </template>

        <template v-else>
            <v-form ref="form" @submit.prevent="save">
                <v-card-text>
                    <v-row>
                        <v-col cols="12" xs="12" sm="12" md="7">
                            <v-text-field
                                v-model="model.name"
                                disabled
                                :label="$t('modules.form.labels.name')"
                            ></v-text-field>
                        </v-col>

                        <v-col cols="12" xs="12" sm="12" md="5">
                            <v-select
                                v-model="model.type"
                                :items="$t('modules.form.fields.type.values')"
                                :label="$t('modules.form.labels.type')"
                                :placeholder="
                                    $t('modules.form.placeholders.type')
                                "
                                :error-messages="TypeErrors"
                                @input="$v.model.type.$touch()"
                                @blur="$v.model.type.$touch()"
                                clearable
                            ></v-select>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" xs="12" sm="12" md="3">
                            <v-select
                                v-model="model.price_type"
                                :items="
                                    $t('modules.form.fields.price-type.values')
                                "
                                :label="$t('modules.form.labels.price-type')"
                                :placeholder="
                                    $t('modules.form.placeholders.price-type')
                                "
                                :error-messages="PriceTypeErrors"
                                @input="$v.model.price_type.$touch()"
                                @blur="$v.model.price_type.$touch()"
                                clearable
                            ></v-select>
                        </v-col>

                        <v-col cols="12" xs="12" sm="12" md="4">
                            <v-currency-field
                                disabled
                                :label="
                                    $t('modules.form.labels.original-price')
                                "
                                v-bind="$t('money-config')"
                                v-model="model.original_price"
                            ></v-currency-field>
                        </v-col>

                        <v-col cols="12" xs="12" sm="12" md="4">
                            <v-radio-group
                                v-model="model.third_party"
                                row
                                :error-messages="ThirdPartyErrors"
                                @change="$v.model.third_party.$touch()"
                                :label="
                                    $t('modules.form.labels.third-party-apps')
                                "
                                dense
                            >
                                <v-radio
                                    :label="$t('form.status.yes')"
                                    color="success"
                                    :value="true"
                                ></v-radio>
                                <v-radio
                                    :label="$t('form.status.no')"
                                    color="red"
                                    :value="false"
                                ></v-radio>
                            </v-radio-group>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" xs="12" sm="12" md="4">
                            <v-currency-field
                                :label="$t('modules.form.labels.partner-price')"
                                v-bind="$t('money-config')"
                                v-model="model.partner_price"
                                :error-messages="PartnerPriceErrors"
                                @input="$v.model.partner_price.$touch()"
                                @blur="$v.model.partner_price.$touch()"
                            ></v-currency-field>
                        </v-col>

                        <v-col cols="12" xs="12" sm="12" md="4">
                            <v-currency-field
                                :label="
                                    $t('modules.form.labels.whitelabel-price')
                                "
                                v-bind="$t('money-config')"
                                v-model="model.whitelabel_price"
                                :error-messages="WhitelabelPriceErrors"
                                @input="$v.model.whitelabel_price.$touch()"
                                @blur="$v.model.whitelabel_price.$touch()"
                            ></v-currency-field>
                        </v-col>

                        <v-col cols="12" xs="12" sm="12" md="4">
                            <v-text-field
                                :label="$t('modules.form.labels.included')"
                                v-model="model.included"
                                type="number"
                                :error-messages="IncludedErrors"
                                @input="$v.model.included.$touch()"
                                @blur="$v.model.included.$touch()"
                            ></v-text-field>
                        </v-col>
                    </v-row>
                    <v-row class="grey lighten-4 pa-4">
                        <v-col cols="12" xs="12" sm="12" md="4">
                            <label class="subtitle-2">
                                <v-icon left>mdi-link-variant</v-icon>
                                {{ $t('modules.form.labels.enable-by') }}
                            </label>
                            <p class="caption">
                                {{
                                    $t('modules.form.hint.enable-by', {
                                        module: model.name,
                                    })
                                }}
                            </p>
                            <v-select
                                v-model="model.enable_by"
                                :items="allModules"
                                placeholder="Selecionar um módulo..."
                                clearable
                                single-line
                            ></v-select>
                        </v-col>

                        <v-col cols="12" xs="12" sm="12" md="4">
                            <label class="subtitle-2">
                                <v-icon left>mdi-link-variant</v-icon>
                                {{ $t('modules.form.labels.activate') }}
                            </label>
                            <p class="caption">
                                {{ $t('modules.form.hint.activate') }}
                            </p>
                            <v-select
                                v-model="model.activate"
                                :items="allModules"
                                placeholder="Selecionar um módulo..."
                                clearable
                                single-line
                            ></v-select>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" xs="12" sm="12" md="4">
                            <label class="subtitle-2">
                                <v-icon left>mdi-file-lock</v-icon>
                                {{ $t('modules.form.labels.partner') }}
                            </label>
                            <v-radio-group
                                v-model="model.partner"
                                row
                                :error-messages="PartnerErrors"
                                @change="$v.model.partner.$touch()"
                            >
                                <v-card outlined class="mr-2 pa-5">
                                    <v-radio
                                        :label="$t('form.status.active')"
                                        color="success"
                                        :value="true"
                                    ></v-radio>
                                </v-card>
                                <v-card outlined class="mr-2 pa-5">
                                    <v-radio
                                        :label="$t('form.status.disabled')"
                                        color="red"
                                        :value="false"
                                    ></v-radio>
                                </v-card>
                            </v-radio-group>
                        </v-col>
                        <v-col cols="12" xs="12" sm="12" md="4">
                            <label class="subtitle-2">
                                <v-icon left>mdi-file-lock</v-icon>
                                {{ $t('modules.form.labels.whitelabel') }}
                            </label>
                            <v-radio-group
                                v-model="model.whitelabel"
                                row
                                :error-messages="WhitelabelErrors"
                                @change="$v.model.whitelabel.$touch()"
                            >
                                <v-card outlined class="mr-2 pa-5">
                                    <v-radio
                                        :label="$t('form.status.active')"
                                        color="success"
                                        :value="true"
                                    ></v-radio>
                                </v-card>
                                <v-card outlined class="mr-2 pa-5">
                                    <v-radio
                                        :label="$t('form.status.disabled')"
                                        color="red"
                                        :value="false"
                                    ></v-radio>
                                </v-card>
                            </v-radio-group>
                        </v-col>
                        <v-col cols="12" xs="12" sm="12" md="4">
                            <label class="subtitle-2">
                                <v-icon left>mdi-file-lock</v-icon>
                                {{ $t('modules.form.labels.update-on-plans') }}
                            </label>
                            <v-radio-group v-model="updateOnPlans" row>
                                <v-card outlined class="mr-2 pa-5">
                                    <v-radio
                                        :label="$t('form.status.yes')"
                                        color="success"
                                        :value="true"
                                    ></v-radio>
                                </v-card>
                                <v-card outlined class="mr-2 pa-5">
                                    <v-radio
                                        :label="$t('form.status.no')"
                                        color="red"
                                        :value="false"
                                    ></v-radio>
                                </v-card>
                            </v-radio-group>
                        </v-col>
                    </v-row>
                </v-card-text>

                <form-actions :loadingSave="loading" />
            </v-form>
        </template>
    </v-card>
</template>

<script>
import CardHeader from '@/components/utils/CardHeader'
import FormActions from '@/components/utils/FormActions'
import BaseModel from '@/models/BaseModel'
import Module from '@/models/plans/Module'
import { required, between, requiredIf } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'
import { mask } from 'vue-the-mask'

export default {
    props: {
        module: {
            type: Object,
            required: true,
            validator: model => {
                return model instanceof BaseModel
            },
        },
        isEdit: {
            type: Boolean,
            default: () => {
                return false
            },
        },
        titlePrefix: String,
    },

    directives: { mask },

    data: () => {
        return {
            loading: false,
            allModules: [],
            updateOnPlans: false,
        }
    },

    components: {
        'card-header': CardHeader,
        'form-actions': FormActions,
    },

    mixins: [validationMixin],

    validations: {
        model: {
            partner_price: {
                required,
                between: between(0, 99999999.99),
            },
            whitelabel_price: {
                requiredIf: requiredIf(function(model) {
                    return model.whitelabel
                }),
                between: between(0, 99999999.99),
            },
            type: { required },
            price_type: { required },
            included: { between: between(0, 100) },
            partner: { required },
            whitelabel: { required },
            third_party: { required },
        },
    },

    methods: {
        async save() {
            this.loading = true

            this.$v.$touch()
            if (this.$v.$invalid) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('form.validation-error'),
                )

                this.loading = false
                return
            }

            let clone = this.model.clone()
            if (typeof clone.activate == 'undefined') {
                clone.activate = null
            }
            if (typeof clone.enable_by == 'undefined') {
                clone.enable_by = null
            }
            clone.update_on_plans = this.updateOnPlans

            let res = null

            try {
                res = await clone.save().catch(() => {})
            } finally {
                this.loading = false
            }

            if (!res) return

            this.$emit('saved')

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('modules.form.feedback.success', { name: clone.name }),
            )
        },

        clear() {
            this.$refs.form.reset()
        },

        async getAllModules() {
            let model = new Module()

            let modules = await model.paginate(50, 1)

            if (!modules) return

            let allModules = modules.data.map(item => {
                return { text: item.name, value: parseInt(item.id) }
            })

            this.allModules = allModules
        },
    },

    created() {
        this.getAllModules()
    },

    computed: {
        model() {
            return this.module
        },

        TypeErrors() {
            const errors = []
            if (!this.$v.model.type.$dirty) return errors

            !this.$v.model.type.required &&
                errors.push(this.$t('modules.form.errors.type.required'))

            return errors
        },
        PriceTypeErrors() {
            const errors = []
            if (!this.$v.model.price_type.$dirty) return errors

            !this.$v.model.price_type.required &&
                errors.push(this.$t('modules.form.errors.price-type.required'))

            return errors
        },
        PartnerPriceErrors() {
            const errors = []
            if (!this.$v.model.partner_price.$dirty) return errors

            !this.$v.model.partner_price.required &&
                errors.push(
                    this.$t('modules.form.errors.partner-price.required'),
                )

            !this.$v.model.partner_price.between &&
                errors.push(
                    this.$t('modules.form.errors.partner-price.between'),
                )

            return errors
        },
        WhitelabelPriceErrors() {
            const errors = []
            if (!this.$v.model.whitelabel_price.$dirty) return errors

            !this.$v.model.whitelabel_price.requiredIf &&
                errors.push(
                    this.$t('modules.form.errors.whitelabel-price.required'),
                )

            !this.$v.model.whitelabel_price.between &&
                errors.push(
                    this.$t('modules.form.errors.whitelabel-price.between'),
                )

            return errors
        },
        IncludedErrors() {
            const errors = []
            if (!this.$v.model.included.$dirty) return errors

            !this.$v.model.included.between &&
                errors.push(this.$t('modules.form.errors.included.between'))

            return errors
        },
        PartnerErrors() {
            const errors = []
            if (!this.$v.model.partner.$dirty) return errors

            !this.$v.model.partner.required &&
                errors.push(this.$t('modules.form.errors.partner.required'))

            return errors
        },
        WhitelabelErrors() {
            const errors = []
            if (!this.$v.model.whitelabel.$dirty) return errors

            !this.$v.model.whitelabel.required &&
                errors.push(this.$t('modules.form.errors.whitelabel.required'))

            return errors
        },

        ThirdPartyErrors() {
            const errors = []
            if (!this.$v.model.third_party.$dirty) return errors

            !this.$v.model.third_party.required &&
                errors.push(this.$t('modules.form.errors.third-party.required'))

            return errors
        },
    },
}
</script>
